import { observer } from "mobx-react-lite";
import { ArrowLeft, Building2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { useTran } from "@/hooks";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common";
import { PATHS } from "@/lib/router";
import { useCompanyStore } from "../hooks";
import { Can } from "@/hooks/useCan/Can";
import { useAuthStore } from "@/features/auth/hooks";

const CompanySetupHeader = observer(
  ({
    isDialog,
    onSampleData,
    onReset,
    progress: progressOverride,
    isCreateMode = false,
  }) => {
    const { t } = useTran();
    const navigate = useNavigate();
    const location = useLocation();
    const { progress: storeProgress } = useCompanyStore();
    const { user } = useAuthStore();
    const permissions = user?.permissions || [];
    const isFromDashboard = location.state?.from === "dashboard";
    const progress = Number.isFinite(progressOverride)
      ? progressOverride
      : storeProgress;

    const actions = (
      <div className="flex flex-wrap items-center gap-2">
        {isFromDashboard ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate(PATHS.DASHBOARD)}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("Back")}
          </Button>
        ) : null}
        {!isDialog && isCreateMode ? (
          <>
            <Can
              resource="company_information"
              action="update"
              userPermissions={permissions}
            >
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onSampleData}
              >
                {t("use_sample_data")}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onReset}
              >
                {t("Reset")}
              </Button>
            </Can>
          </>
        ) : null}
      </div>
    );

    return (
      <div className="space-y-4">
        <PageHeader
          title={t("lets_set_up_company")}
          description={t("company_setup_desc")}
          icon={Building2}
          actions={actions}
        />
        <div className="flex items-center gap-3">
          <div className="h-1.5 w-full flex-1 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-gradient-to-r from-primary to-emerald-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-sm font-semibold text-foreground">
            {progress}%
          </span>
        </div>
      </div>
    );
  },
);

export default CompanySetupHeader;
